import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Sparkles, RefreshCw, Loader2, Droplet, Heart, Brain, Ribbon, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const SPECIALTY_ICON = {
  nephrology: Droplet,
  cardiology: Heart,
  neurology:  Brain,
  oncology:   Ribbon,
};
const SPECIALTY_ACCENT = {
  nephrology: "#4ADE80",
  cardiology: "#F87171",
  neurology:  "#A78BFA",
  oncology:   "#FBBF24",
};
const SPECIALTY_LABEL = {
  nephrology: "Nephrology",
  cardiology: "Cardiology",
  neurology:  "Neurology",
  oncology:   "Oncology",
};

/**
 * AIDigestCard — Claude-generated weekly digest for a single specialty.
 * Renders on the Command Center. Users can flip between active specialties
 * with the tab strip and refresh on demand.
 */
export default function AIDigestCard({ activeSpecialties }) {
  const specialties = (activeSpecialties || []).filter(
    (s) => SPECIALTY_LABEL[s]
  );
  const [selected, setSelected] = useState(specialties[0] || "cardiology");
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Keep `selected` in sync with the available specialties (once tiles load)
  useEffect(() => {
    if (specialties.length > 0 && !specialties.includes(selected)) {
      setSelected(specialties[0]);
    }
  }, [specialties, selected]);

  const load = useCallback(async (specialty, force = false) => {
    if (!specialty) return;
    force ? setRefreshing(true) : setLoading(true);
    try {
      const { data } = await api.get(
        `/specialties/${specialty}/digest${force ? "?refresh=1" : ""}`
      );
      setDigest(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load digest");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(selected); }, [load, selected]);

  if (specialties.length === 0) {
    return null;
  }

  const accent = SPECIALTY_ACCENT[selected] || "#A3ACA7";
  const Icon   = SPECIALTY_ICON[selected] || Sparkles;
  const shortText = digest?.digest_text?.slice(0, 380) || "";
  const isLong    = (digest?.digest_text || "").length > 380;

  return (
    <div className="ros-card" data-testid="ai-digest-card"
         style={{ borderColor: `${accent}55`, background: `${accent}08` }}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center"
               style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
            <Sparkles size={14} style={{ color: accent }} />
          </div>
          <div>
            <div className="ros-label" style={{ fontSize: 10, color: accent }}>
              AI Weekly Digest
            </div>
            <div className="font-heading text-base text-white mt-0.5">
              This week in {SPECIALTY_LABEL[selected]}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load(selected, true)}
          disabled={refreshing || loading}
          data-testid="ai-digest-refresh"
          title="Regenerate with latest data"
          className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#272E2B] text-[#A3ACA7] hover:text-white hover:border-[#4B5563] transition-colors disabled:opacity-50"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Specialty tab strip (only if patient has >1 specialty digest) */}
      {specialties.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ai-digest-tabs">
          {specialties.map((s) => {
            const on = s === selected;
            const c  = SPECIALTY_ACCENT[s];
            const I  = SPECIALTY_ICON[s];
            return (
              <button
                key={s}
                onClick={() => { setSelected(s); setExpanded(false); }}
                data-testid={`ai-digest-tab-${s}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 ${
                  on ? "text-black border-transparent"
                     : "text-[#A3ACA7] border-[#272E2B] hover:text-white"
                }`}
                style={on ? { background: c } : {}}
              >
                <I size={11} /> {SPECIALTY_LABEL[s]}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#A3ACA7]"
               data-testid="ai-digest-loading">
            <Loader2 size={14} className="animate-spin" style={{ color: accent }} />
            Assembling your weekly digest…
          </div>
        ) : digest?.digest_text ? (
          <div data-testid="ai-digest-body">
            <div className="text-sm text-[#E5E7EB] leading-relaxed whitespace-pre-line">
              {expanded ? digest.digest_text : shortText + (isLong ? "…" : "")}
            </div>
            {isLong && (
              <button type="button" onClick={() => setExpanded((x) => !x)}
                      data-testid="ai-digest-toggle-expand"
                      className="mt-2 text-[11px] flex items-center gap-1"
                      style={{ color: accent }}>
                <ChevronDown size={12} className={expanded ? "rotate-180 transition-transform" : "transition-transform"} />
                {expanded ? "Show less" : "Read the full digest"}
              </button>
            )}
            <div className="mt-3 flex items-center gap-3 text-[10px] text-[#7A8480]">
              <span data-testid="ai-digest-model-badge">
                <Icon size={10} className="inline mr-1" style={{ color: accent }} />
                {digest.model === "static-empty-state" ? "No data yet" : "Med Nexus AI"}
              </span>
              {digest.week_key && <span>· Week {digest.week_key}</span>}
              {digest.cached && <span>· Cached</span>}
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#A3ACA7]">No digest available.</div>
        )}
      </div>
    </div>
  );
}
